import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckCircleIcon } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from 'recharts';
type CellValue = {
  value: string;
  status: 'best' | 'good' | 'neutral' | 'bad';
};
type Row = {
  metric: string;
  claudeCode: CellValue;
  copilot: CellValue;
  cursor: CellValue;
  internal: CellValue;
};
const tableData: Row[] = [{
  metric: 'Cycle Time Delta',
  claudeCode: {
    value: '-1.8d',
    status: 'best'
  },
  copilot: {
    value: '-1.2d',
    status: 'good'
  },
  cursor: {
    value: '-2.1d',
    status: 'best'
  },
  internal: {
    value: '-0.4d',
    status: 'neutral'
  }
}, {
  metric: 'PR Review Speed',
  claudeCode: {
    value: '+22%',
    status: 'good'
  },
  copilot: {
    value: '+15%',
    status: 'neutral'
  },
  cursor: {
    value: '+18%',
    status: 'good'
  },
  internal: {
    value: '-5%',
    status: 'bad'
  }
}, {
  metric: 'Rework Rate',
  claudeCode: {
    value: '9%',
    status: 'best'
  },
  copilot: {
    value: '14%',
    status: 'neutral'
  },
  cursor: {
    value: '11%',
    status: 'good'
  },
  internal: {
    value: '21%',
    status: 'bad'
  }
}, {
  metric: 'Incident Correlation',
  claudeCode: {
    value: 'Low',
    status: 'best'
  },
  copilot: {
    value: 'Medium',
    status: 'neutral'
  },
  cursor: {
    value: 'Low',
    status: 'best'
  },
  internal: {
    value: 'High',
    status: 'bad'
  }
}, {
  metric: 'Deploy Frequency Impact',
  claudeCode: {
    value: '+31%',
    status: 'best'
  },
  copilot: {
    value: '+18%',
    status: 'good'
  },
  cursor: {
    value: '+27%',
    status: 'good'
  },
  internal: {
    value: '+8%',
    status: 'neutral'
  }
}, {
  metric: 'Cost per AI-Active Eng.',
  claudeCode: {
    value: '$340/mo',
    status: 'neutral'
  },
  copilot: {
    value: '$190/mo',
    status: 'best'
  },
  cursor: {
    value: '$240/mo',
    status: 'good'
  },
  internal: {
    value: '$580/mo',
    status: 'bad'
  }
}];
const chartData = [{
  vendor: 'Cursor',
  delta: 2.1,
  fill: '#A855F7'
}, {
  vendor: 'Claude Code',
  delta: 1.8,
  fill: '#F97316'
}, {
  vendor: 'Copilot',
  delta: 1.2,
  fill: '#10B981'
}, {
  vendor: 'Internal',
  delta: 0.4,
  fill: '#6B7280'
}];
function StatusCell({
  cell
}: {
  cell: CellValue;
}) {
  const statusStyles = {
    best: 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/20',
    good: 'text-green-400 bg-green-400/10 border border-green-400/20',
    neutral: 'text-gray-400 bg-gray-400/10 border border-gray-400/20',
    bad: 'text-red-400 bg-red-400/10 border border-red-400/20'
  };
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-semibold ${statusStyles[cell.status]}`} data-id="element-280">
      {cell.status === 'best' && <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-281" />}
      {cell.value}
    </span>;
}
const vendors = [{
  key: 'claudeCode',
  label: 'Claude Code',
  color: '#F97316'
}, {
  key: 'copilot',
  label: 'GitHub Copilot',
  color: '#10B981'
}, {
  key: 'cursor',
  label: 'Cursor',
  color: '#A855F7'
}, {
  key: 'internal',
  label: 'Internal Agents',
  color: '#6B7280'
}];
export function VendorComparisonSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-24 px-6 bg-[#080C14] relative" data-id="element-282">
      <div className="max-w-6xl mx-auto" data-id="element-283">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-4" data-id="element-284">
          <span className="text-xs font-bold tracking-[0.25em] text-cyan-400 uppercase" data-id="element-285">
            Vendor AI Comparison
          </span>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.1,
        duration: 0.6
      }} className="mb-12" data-id="element-286">
          <h2 className="text-4xl md:text-5xl font-black text-white tracking-tight mb-3" data-id="element-287">
            Compare AI Tools Side-by-Side
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl" data-id="element-288">
            Executives can finally answer:{' '}
            <span className="text-white font-medium" data-id="element-289">
              Which AI vendor delivers measurable ROI?
            </span>
          </p>
        </motion.div>

        {/* Comparison table */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.2,
        duration: 0.6
      }} className="mb-8 rounded-2xl border border-[#1E2D45] overflow-hidden" data-id="element-290">
          <div className="overflow-x-auto" data-id="element-291">
            <table className="w-full" data-id="element-292">
              <thead data-id="element-293">
                <tr className="bg-[#0D1421] border-b border-[#1E2D45]" data-id="element-294">
                  <th className="text-left px-6 py-4 text-gray-500 text-xs font-semibold tracking-wider uppercase w-48" data-id="element-295">
                    Metric
                  </th>
                  {vendors.map(v => <th key={v.key} className="px-4 py-4 text-center" data-id="element-296">
                      <div className="flex flex-col items-center gap-1" data-id="element-297">
                        <span className="w-2 h-2 rounded-full" style={{
                      backgroundColor: v.color
                    }} data-id="element-298" />
                        <span className="text-white text-sm font-semibold whitespace-nowrap" data-id="element-299">
                          {v.label}
                        </span>
                      </div>
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-300">
                {tableData.map((row, i) => <motion.tr key={row.metric} initial={{
                opacity: 0,
                x: -20
              }} animate={isInView ? {
                opacity: 1,
                x: 0
              } : {}} transition={{
                delay: 0.3 + i * 0.07,
                duration: 0.4
              }} className="border-b border-[#1E2D45] last:border-0 hover:bg-[#0D1421]/50 transition-colors" data-id="element-301">
                    <td className="px-6 py-4 text-gray-300 text-sm font-medium" data-id="element-302">
                      {row.metric}
                    </td>
                    <td className="px-4 py-4 text-center" data-id="element-303">
                      <StatusCell cell={row.claudeCode} data-id="element-304" />
                    </td>
                    <td className="px-4 py-4 text-center" data-id="element-305">
                      <StatusCell cell={row.copilot} data-id="element-306" />
                    </td>
                    <td className="px-4 py-4 text-center" data-id="element-307">
                      <StatusCell cell={row.cursor} data-id="element-308" />
                    </td>
                    <td className="px-4 py-4 text-center" data-id="element-309">
                      <StatusCell cell={row.internal} data-id="element-310" />
                    </td>
                  </motion.tr>)}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Bar chart */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.6,
        duration: 0.6
      }} className="bg-[#0D1421] border border-[#1E2D45] rounded-2xl p-6" data-id="element-311">
          <div className="mb-6" data-id="element-312">
            <div className="text-white font-semibold" data-id="element-313">
              Cycle Time Reduction by Vendor
            </div>
            <div className="text-gray-500 text-sm" data-id="element-314">
              Days saved per sprint (higher is better)
            </div>
          </div>
          <div className="h-48" data-id="element-315">
            <ResponsiveContainer width="100%" height="100%" data-id="element-316">
              <BarChart data={chartData} barSize={48} layout="vertical" data-id="element-317">
                <XAxis type="number" tick={{
                fill: '#6B7280',
                fontSize: 11
              }} axisLine={false} tickLine={false} domain={[0, 3]} data-id="element-318" />
                <YAxis type="category" dataKey="vendor" tick={{
                fill: '#9CA3AF',
                fontSize: 12
              }} axisLine={false} tickLine={false} width={90} data-id="element-319" />
                <Tooltip contentStyle={{
                backgroundColor: '#0D1421',
                border: '1px solid #1E2D45',
                borderRadius: 8
              }} labelStyle={{
                color: '#fff'
              }} formatter={(v: number) => [`${v} days`, 'Cycle Time Reduction']} data-id="element-320" />
                <Bar dataKey="delta" radius={[0, 4, 4, 0]} data-id="element-321">
                  {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.fill} data-id="element-322" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>
    </section>;
}